import { createSlice, Dispatch } from "@reduxjs/toolkit";
import { auth } from "../../../../services";
import { IRootState } from "../../../../models/interface";
import { IAuthState } from "./../../../../models/interface/IAuthState";
import {
  ACCESS_TOKEN,
  ROUTER_PATH,
  SIGN_UP_VALUES,
  USER_INFO,
} from "../../../../common/constant";
import i18n from "../../../../i18n";
import { localizeRouteKey } from "../../../../shared/helpers";
import { setLocalStorageUserInfo } from "../../../../utils/helpers";
import { cleanWishesListLocal } from "../../../../services/profile";

const initialState: IAuthState = {};

export const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    authLoading: (state) => {
      state.loading = true;
    },

    authResetStatus: (state) => {
      state.loading = false;
      state.status = 0;
      state.message = "";
    },

    //sign-in
    authSignInSuccess: (state, action) => {
      state.loading = false;
      state.status = 200;
      state.message = i18n.t("point_status_success");
      state.accessToken = action.payload.token;
      state.userInfo = action.payload.user_info;
    },
    authSignInFail: (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      state.message = action.payload.message;
    },

    //sign-up
    authSignUpSuccess: (state, action) => {
      state.loading = false;
      state.status = 200;
      state.message = action.payload;
    },
    authSignUpFail: (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      state.message = action.payload.message;
    },

    //confirm-otp
    authConfirmOTPSuccess: (state, action) => {
      state.loading = false;
      state.status = 200;
      state.message = i18n.t("point_status_success");
      state.accessToken = action.payload.token;
      state.userInfo = action.payload.user_info;
    },
    authConfirmOTPFail: (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      state.message = action.payload.message;
    },

    //resend-otp
    authResendOTPSuccess: (state, action) => {
      state.loading = false;
      state.status = 200;
      state.message = action.payload;
    },
    authResendOTPFail: (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      state.message = action.payload.message;
    },

    //forgot-password
    authForgotPasswordSuccess: (state, action) => {
      state.loading = false;
      state.status = 200;
      state.message = action.payload;
    },
    authForgotPasswordFail: (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      state.message = action.payload.message;
    },

    //sign-out
    authSignOutSuccess: (state, action) => {
      state.loading = false;
      state.status = 200;
      state.message = i18n.t("point_status_success");
      state.accessToken = "";
      state.userInfo = null;
    },
    authSignOutFail: (state, action) => {
      state.loading = false;
      state.status = action.payload.status;
      state.message = action.payload.message;
    },
  },
});

export const {
  authLoading,
  authSignInSuccess,
  authSignInFail,
  authSignUpSuccess,
  authSignUpFail,
  authResetStatus,
  authConfirmOTPSuccess,
  authConfirmOTPFail,
  authResendOTPSuccess,
  authResendOTPFail,
  authForgotPasswordSuccess,
  authForgotPasswordFail,
  authSignOutSuccess,
  authSignOutFail,
} = authSlice.actions;

export const resetStatus = () => (dispatch: Dispatch) => {
  dispatch(authResetStatus());
};

export const signIn = (query: any, onFailed: Function) => (dispatch: Dispatch) => {
  dispatch(authResetStatus());
  dispatch(authLoading());

  auth.authSignIn(query, (data, err) => {
    if (data) {
      localStorage.setItem(ACCESS_TOKEN, data.token);
      // localStorage.setItem(USER_INFO, JSON.stringify(data.user_info));

      setLocalStorageUserInfo(data.user_info, []);

      dispatch(authSignInSuccess(data));
    } else {
      dispatch(authSignInFail(err));
      onFailed?.();
    }
  });
};

export const signUp = (query: any, onFailed: Function) => (dispatch: Dispatch) => {
  dispatch(authResetStatus());
  dispatch(authLoading());

  auth.authSignUp(query, (data, err) => {
    if (data) {
      dispatch(authSignUpSuccess(data));
    } else {
      dispatch(authSignUpFail(err));
      onFailed?.();
    }
  });
};

export const confirmOTP = (query: any, onFailed: Function) => (dispatch: Dispatch) => {
  dispatch(authResetStatus());
  dispatch(authLoading());

  auth.authConfirmOTP(query, (data, err) => {
    if (data) {
      localStorage.setItem(ACCESS_TOKEN, data.token);
      // localStorage.setItem(USER_INFO, JSON.stringify(data.user_info));
      setLocalStorageUserInfo(data.user_info, []);

      dispatch(authConfirmOTPSuccess(data));

      sessionStorage.removeItem(SIGN_UP_VALUES);
    } else {
      dispatch(authConfirmOTPFail(err));
      onFailed?.();
    }
  });
};

export const resendOTP = (query: any) => (dispatch: Dispatch) => {
  dispatch(authResetStatus());
  // dispatch(authLoading());

  auth.authResendOTP(query, (data, err) => {
    if (data) {
      dispatch(authResendOTPSuccess(data));
    } else {
      dispatch(authResendOTPFail(err));
    }
  });
};

export const forgotPassword = (query: any, onFailed: Function) => (dispatch: Dispatch) => {
  dispatch(authResetStatus());
  // dispatch(authLoading());

  auth.authForgotPassword(query, (data, err) => {
    if (data) {
      dispatch(authForgotPasswordSuccess(data));
    } else {
      dispatch(authForgotPasswordFail(err));
      onFailed?.();
    }
  });
};

export const signOut = () => (dispatch: Dispatch) => {
  dispatch(authResetStatus());
  dispatch(authLoading());

  auth.authSignOut((data, err) => {
    if (data) {
      localStorage.removeItem(ACCESS_TOKEN);
      localStorage.removeItem(USER_INFO);
      cleanWishesListLocal();
      dispatch(authSignOutSuccess(data));

      window.location.href = localizeRouteKey(ROUTER_PATH.HOME);
    } else {
      dispatch(authSignOutFail(err));
    }
  });
};

export const selectAuth = (state: IRootState) => state.auth;

export default authSlice.reducer;
